import {
  AboutModal,
  AccountCard,
  ApprovalSettingsSection,
  type ApprovalSettingsSource,
  CompanySetupCard,
  DrydockSettingsSection,
  LanguageCard,
  MasterDeputySettingsSection,
  type MasterDeputySettingsSource,
  Panel,
  PmsSettingsSection,
  ProvisionSettingsSection,
  RaSettingsSection,
  RiskMatrixSettingsSection,
  type RiskMatrixSettingsSource,
  SettingsLayout,
  SoftwareCard,
  UsersCard,
  useWhatsNew,
  WhatsNewModal,
} from '@felix/ui';
import { Alert, Text } from '@mantine/core';
import { IconBrush } from '@tabler/icons-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { officeApi } from '../api.js';
import { BrandingForm } from '../components/BrandingForm.js';
import { CompanyInfoCard } from '../components/CompanyInfoCard.js';
import { Shell } from '../components/Shell.js';
import { DisableTwoStepModal, EnableTwoStepModal } from '../components/TwoStepModals.js';
import { officeDrydockSettingsSource } from '../drydock-source.js';
import { LANGUAGES } from '../i18n.js';
import { officePmsSource } from '../pms-source.js';
import { officeProvisionsSource } from '../provisions-source.js';
import { officeRaSettingsSource } from '../risk-source.js';
import { PATHS } from '../routing.js';
import { useSession } from '../session.js';
import { useSetupStatus } from './SetupPages.js';

/**
 * Ayarlar (M6f G6-33): gemi uygulamasıyla aynı düzen ve kart sırası. Ana sütunda dil ve bölge,
 * hesap ve şirket markası (ADR 0019 §4; yalnız web); özet sütununda yazılım.
 */
export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const session = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  // Parola sayfasından dönüş (ortak 2026-10-30)
  const passwordChanged =
    (location.state as { passwordChanged?: boolean } | null)?.passwordChanged === true;
  const user = session.me?.user;
  // Şirket kurulumu kartı yalnız settings.edit yetkisinde (ADR 0042 §22)
  const setupStatus = useSetupStatus();
  const [about, setAbout] = useState(false);
  // İki adımlı doğrulama isteğe bağlı (sahip 2026-11-01): açma ve kapatma pencereleri
  const [twoStepDialog, setTwoStepDialog] = useState<'enable' | 'disable' | null>(null);
  const [twoStepOff, setTwoStepOff] = useState(false);
  const twoStep = session.me?.twoStep;
  const whatsNew = useWhatsNew('office');
  // Ayarlar ▸ Onaylar (ADR 0056 §4): yalnız settings.edit düzenler (kurulum durumu ondadır)
  const canEditSettings = setupStatus !== null;
  const approvalSource = useMemo<ApprovalSettingsSource>(
    () => ({
      load: () => officeApi.approvalSettings(),
      ...(canEditSettings
        ? {
            save: async (rowVersion, reauthAlways) => {
              const saved = await officeApi.saveApprovalSettings({ rowVersion, reauthAlways });
              await session.refresh().catch(() => null);
              return saved;
            },
          }
        : {}),
    }),
    [canEditSettings, session.refresh],
  );
  // Ayarlar ▸ Kaptanın vekili (ADR 0127 §3): herkes görür; settings.edit seçer
  const deputySource = useMemo<MasterDeputySettingsSource>(
    () => ({
      load: () => officeApi.masterDeputy(),
      ...(canEditSettings
        ? { save: (rowVersion, roleId) => officeApi.saveMasterDeputy({ rowVersion, roleId }) }
        : {}),
    }),
    [canEditSettings],
  );
  // Ayarlar ▸ Dry dock (ADR 0129 Ek Faz 3; sahip kararı 2026-10-26): herkes görür; settings.edit
  // değiştirir
  const drydockSource = useMemo(
    () => officeDrydockSettingsSource(canEditSettings),
    [canEditSettings],
  );
  // Settings ▸ Risk Matrix (K1; ADR 0085 §2): herkes görür; settings.edit seçer ve yayımlar
  const riskMatrixSource = useMemo<RiskMatrixSettingsSource>(
    () => ({
      load: () => officeApi.riskMatrix(),
      ...(canEditSettings
        ? {
            select: (rowVersion, profileId) =>
              officeApi.selectRiskMatrix({ rowVersion, profileId }),
            publish: (profileId, expectedVersion, definition, reason) =>
              officeApi.publishRiskMatrix(profileId, { expectedVersion, definition, reason }),
          }
        : {}),
    }),
    [canEditSettings],
  );

  const provisionOffice = useMemo(() => officeProvisionsSource().office, []);

  const signOut = async () => {
    await officeApi.logout().catch(() => undefined);
    session.setStage(null);
    navigate(PATHS.login, { replace: true });
  };

  return (
    <Shell title={t('menu.settings')}>
      <SettingsLayout
        title={t('menu.settings')}
        description={t('settingsScreen.subtitleOffice')}
        main={
          <>
            {passwordChanged ? (
              <Alert color="teal" role="status">
                {t('password.changed')}
              </Alert>
            ) : null}
            <CompanySetupCard status={setupStatus} />
            {/* Ayarlar ▸ Kullanıcılar (ortak 2026-10-30): users.view olan herkes, ör. Crew Manager */}
            <UsersCard visible={session.me?.canViewUsers === true} />
            <LanguageCard
              language={i18n.resolvedLanguage ?? 'en'}
              languages={LANGUAGES.map((language) => ({
                value: language,
                label: t(`language.name.${language}`),
              }))}
              onLanguage={(language) => void i18n.changeLanguage(language)}
            />
            {twoStepOff ? (
              <Alert color="teal" role="status">
                {t('twoStep.disabled')}
              </Alert>
            ) : null}
            <AccountCard
              name={user?.fullName ?? null}
              email={user?.email ?? null}
              // Eski sunucu `twoStep` göndermez: o zaman TOTP zorunludur, tek satırlık özet kalır
              security={t('settings.account.securityValue')}
              twoStep={
                twoStep
                  ? {
                      ...twoStep,
                      onEnable: () => {
                        setTwoStepOff(false);
                        setTwoStepDialog('enable');
                      },
                      onDisable: () => {
                        setTwoStepOff(false);
                        setTwoStepDialog('disable');
                      },
                    }
                  : undefined
              }
              // Kendi parolasını değiştirme (ortak 2026-10-30): mevcut parola istenir
              onChangePassword={() => navigate(PATHS.password)}
              onSignOut={() => void signOut()}
            />
            {/* Ayarlar ▸ Şirket bilgileri (ADR 0140 §4): yalnız şirket yöneticisi, salt okunur */}
            {canEditSettings ? <CompanyInfoCard /> : null}
            <Panel
              title={t('settingsScreen.branding.title')}
              icon={<IconBrush size={18} stroke={1.8} />}
              divider
            >
              <Text fz="sm" c="var(--felix-text-secondary)" mb="md">
                {t('settings.branding.text')}
              </Text>
              <BrandingForm />
            </Panel>
            {/* Settings ▸ PMS (plan M7 Task 21c; K32): şirketin eşikleri ve varsayılan yapı */}
            <PmsSettingsSection source={officePmsSource} />
            <ApprovalSettingsSection source={approvalSource} />
            <MasterDeputySettingsSection source={deputySource} />
            <DrydockSettingsSection source={drydockSource} />
            <RiskMatrixSettingsSection source={riskMatrixSource} />
            {/* Settings ▸ Risk Assessment (K7; ADR 0095 §3): gözden geçirme, yetki ve onay eşikleri */}
            <RaSettingsSection source={officeRaSettingsSource} />
            {/* Settings ▸ Provisions (ADR 0099 §9): son kullanma uyarı penceresi */}
            {provisionOffice ? (
              <ProvisionSettingsSection office={provisionOffice} canEdit={canEditSettings} />
            ) : null}
          </>
        }
        aside={
          <SoftwareCard
            version={__APP_VERSION__}
            status={{ state: 'office' }}
            onAbout={() => setAbout(true)}
            onWhatsNew={whatsNew.open}
            whatsNewUnseen={whatsNew.unseen}
          />
        }
      />
      <AboutModal
        opened={about}
        onClose={() => setAbout(false)}
        version={__APP_VERSION__}
        build={__APP_BUILD__}
      />
      {/*
        İki adımlı doğrulama (sahip 2026-11-01): başarıdan sonra profil yenilenir, rozet değişir.
        Durum başka sekmede değişmişse (zaten açık, zaten kapalı) hatadan sonra da yenilenir.
      */}
      <EnableTwoStepModal
        opened={twoStepDialog === 'enable'}
        onClose={() => setTwoStepDialog(null)}
        onEnabled={() => session.refresh()}
        onStale={() => session.refresh()}
      />
      <DisableTwoStepModal
        opened={twoStepDialog === 'disable'}
        onClose={() => setTwoStepDialog(null)}
        onDisabled={async () => {
          setTwoStepOff(true);
          await session.refresh();
        }}
        onStale={() => session.refresh()}
      />
      {/* Yenilikler (M17, ADR 0064) */}
      <WhatsNewModal
        opened={whatsNew.opened}
        onClose={whatsNew.close}
        notes={whatsNew.notes}
        unseenIds={whatsNew.unseenIds}
      />
    </Shell>
  );
}
